// src/App.jsx
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import NotificationProvider from "./NotificationContext";
import { ToastContainer } from "react-toastify";

import ShareFolderPage from "./pages/ShareFolderPage";
import ShareFilePage   from "./pages/ShareFilePage";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Buckets from "./components/Buckets";
import Users from "./components/Users";
import AccessKeys from "./components/AccessKeys";
import Settings from "./components/Settings";
import Objects from "./components/Objects";
import Success from "./pages/Success";
import Upgrade from "./pages/Upgrade";
import Register from "./pages/Register";
import EmailSent from "./pages/EmailSent";
import CompleteRegistration from "./pages/CompleteRegistration";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import ResetPassword from "./pages/ResetPassword";
import CensusIntelligenceLookup from "./pages/CensusIntelligenceLookup";

import Layout from "./components/Layout";
import UploadStatusPanel from "./components/UploadStatusPanel";

// Fotos
import PhotosIndex from "./components/photos/PhotosIndex";
import PhotosPage from "./components/photos/PhotosPage";

// >>> PDF Viewer (página pública)
import PdfViewer from "./pages/PdfViewer";

import "./theme.css";
import "react-toastify/dist/ReactToastify.css";

export default function App() {
  const [panelMinimized, setPanelMinimized] = useState(false);

  return (
    <NotificationProvider>
      <ToastContainer position="top-right" autoClose={3000} />
      <MainRoutes
        panelMinimized={panelMinimized}
        setPanelMinimized={setPanelMinimized}
      />
    </NotificationProvider>
  );
}

// /photos/:albumId
function PhotosRoute({ panelMinimized, setPanelMinimized }) {
  const { albumId } = useParams();
  return (
    <Layout panelMinimized={panelMinimized} setPanelMinimized={setPanelMinimized}>
      <PhotosPage albumId={albumId} />
    </Layout>
  );
}

function MainRoutes({ panelMinimized, setPanelMinimized }) {
  const { userInfo, loading } = useAuth();
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    if (!loading && userInfo?.username) {
      const token = localStorage.getItem("token");
      setCredentials(token ? { accessKey: userInfo.username, secretKey: token } : null);
    }
  }, [loading, userInfo]);

  if (loading) return null;

  return (
    <>
      <Routes>
        {/* ---------- Públicas ---------- */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/email-enviado" element={<EmailSent />} />
        <Route path="/complete-registration" element={<CompleteRegistration />} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/share/:shareId" element={<ShareFolderPage />} />
        <Route path="/f/:code" element={<ShareFilePage />} />

        {/* PDF Viewer público (sem exigir login) */}
        <Route path="/pdf/:bucketName/*" element={<PdfViewer />} />
        <Route path="/pdf-viewer" element={<PdfViewer />} />

        {/* Raiz -> dashboard/login */}
        <Route
          path="/"
          element={<Navigate to={credentials ? "/dashboard" : "/login"} replace />}
        />

        {/* ---------- Privadas ---------- */}
        {credentials && (
          <>
            <Route
              path="/dashboard"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <Dashboard credentials={credentials} />
                </Layout>
              }
            />

            <Route
              path="/buckets"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <Buckets credentials={credentials} />
                </Layout>
              }
            />

            <Route
              path="/buckets/:bucketName/objects/*"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <Objects />
                </Layout>
              }
            />

            <Route
              path="/users"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <Users credentials={credentials} />
                </Layout>
              }
            />

            <Route
              path="/access-keys"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <AccessKeys credentials={credentials} />
                </Layout>
              }
            />

            <Route
              path="/settings"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <Settings />
                </Layout>
              }
            />

            <Route
              path="/market-intelligence/census"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <CensusIntelligenceLookup />
                </Layout>
              }
            />

            <Route
              path="/success"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <Success />
                </Layout>
              }
            />

            {/* Fotos */}
            <Route path="/photos">
              <Route
                index
                element={
                  <Layout
                    panelMinimized={panelMinimized}
                    setPanelMinimized={setPanelMinimized}
                  >
                    <PhotosIndex />
                  </Layout>
                }
              />
              <Route
                path=":albumId"
                element={
                  <PhotosRoute
                    panelMinimized={panelMinimized}
                    setPanelMinimized={setPanelMinimized}
                  />
                }
              />
            </Route>

            <Route
              path="/upgrade"
              element={
                <Layout
                  panelMinimized={panelMinimized}
                  setPanelMinimized={setPanelMinimized}
                >
                  <Upgrade />
                </Layout>
              }
            />
          </>
        )}
      </Routes>

      {/* PAINEL GLOBAL DE UPLOADS */}
      <UploadStatusPanel
        minimized={panelMinimized}
        setMinimized={setPanelMinimized}
      />
    </>
  );
}
